import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { eventService } from '../../services/eventService';
import './PastEvents.css';

const PastEvents = () => {
  const [selectedYear, setSelectedYear] = useState('');
  const [eventsByYear, setEventsByYear] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [imageErrors, setImageErrors] = useState({});

  const getFullImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      return imageUrl;
    }
    if (imageUrl.startsWith('/nlnapi/')) {
      return `https://nlngraclub.com${imageUrl}`;
    }
    if (imageUrl.startsWith('/uploads/')) {
      return `https://nlngraclub.com${imageUrl}`;
    }
    if (imageUrl.startsWith('/')) {
      return `https://nlngraclub.com${imageUrl}`;
    }
    return `https://nlngraclub.com/${imageUrl}`;
  };

  const handleImageError = (eventId) => {
    setImageErrors(prev => ({ ...prev, [eventId]: true }));
  };

  const formatEventDate = (dateString) => {
    if (!dateString) return 'Date TBA';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Date TBA';
      return date.toLocaleDateString('en-US', { 
        month: 'long', 
        day: 'numeric', 
        year: 'numeric' 
      });
    } catch (e) {
      return 'Date TBA';
    }
  };

  const handleViewDetails = (event) => {
    setSelectedEvent(event);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setTimeout(() => setSelectedEvent(null), 300);
  };

  useEffect(() => {
    const fetchPastEvents = async () => {
      setLoading(true);
      try {
        console.log('Fetching past events from API...');
        // Get all events (upcoming=false to get past events too)
        const response = await eventService.getEvents(false, 200);
        console.log('API Response:', response);
        
        if (response && response.success && response.events && response.events.length > 0) {
          // Group events by year
          const grouped = {};
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          
          response.events.forEach(event => {
            const eventDate = new Date(event.event_date);
            eventDate.setHours(0, 0, 0, 0);
            
            // Only include past events (date < today) - NO DEFAULTS
            if (eventDate < today) {
              const year = eventDate.getFullYear().toString();
              if (!grouped[year]) {
                grouped[year] = [];
              }
              
              // Use ONLY data from API - NO DEFAULTS OR FALLBACKS
              grouped[year].push({
                id: event.id,
                title: event.title,
                date: event.event_date,
                formattedDate: formatEventDate(event.event_date),
                category: event.event_type,
                image_url: event.image_url,
                attendees: event.current_attendees || null,
                highlights: event.description,
                description: event.description,
                location: event.location,
                event_time: event.event_time,
                end_time: event.end_time,
                status: event.status,
                max_attendees: event.max_attendees,
                registered_count: event.registered_count
              });
            }
          });
          
          // Sort years in descending order (most recent first)
          const sortedYears = Object.keys(grouped).sort((a, b) => b - a);
          const sortedGrouped = {};
          sortedYears.forEach(year => {
            sortedGrouped[year] = grouped[year];
          });
          
          setEventsByYear(sortedGrouped);
          
          // Set selected year to the most recent year with events
          if (sortedYears.length > 0) {
            setSelectedYear(sortedYears[0]);
          }
        } else {
          console.log('No events found in API response');
          setEventsByYear({});
        }
      } catch (error) {
        console.error('Error fetching past events:', error);
        setEventsByYear({});
      } finally {
        setLoading(false);
      }
    };
    
    fetchPastEvents();
  }, []);

  const years = Object.keys(eventsByYear);
  const selectedEvents = selectedYear ? eventsByYear[selectedYear] || [] : [];

  if (loading) {
    return (
      <div className="page">
        <div className="page-hero">
          <div className="container">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <span className="eyebrow">Memories</span>
              <h1>Past Events</h1>
              <p className="section-sub">Relive the memorable moments from our previous events</p>
            </motion.div>
          </div>
        </div>
        <section className="section">
          <div className="container">
            <div className="loading-state">
              <i className="fas fa-spinner fa-spin"></i>
              <p>Loading past events...</p>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-hero">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="eyebrow">Memories</span>
            <h1>Past Events</h1>
            <p className="section-sub">Relive the memorable moments from our previous events</p>
          </motion.div>
        </div>
      </div>

      <section className="section">
        <div className="container">
          {years.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-calendar-times"></i>
              <p>No past events found</p>
              <p className="empty-sub">Check back soon for event archives!</p>
            </div>
          ) : (
            <>
              <div className="year-selector">
                {years.map(year => (
                  <motion.button
                    key={year}
                    className={`year-btn ${selectedYear === year ? 'active' : ''}`}
                    onClick={() => setSelectedYear(year)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {year}
                  </motion.button>
                ))}
              </div>

              <div className="past-events-grid">
                {selectedEvents.map((event, index) => (
                  <motion.div
                    key={event.id}
                    className="past-event-card"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ y: -8 }}
                  >
                    <div className="past-event-image">
                      {event.image_url && !imageErrors[event.id] ? (
                        <img 
                          src={getFullImageUrl(event.image_url)} 
                          alt={event.title}
                          onError={() => handleImageError(event.id)}
                        />
                      ) : (
                        <div className="no-image">
                          <i className="fas fa-calendar-alt"></i>
                          <span>No Image</span>
                        </div>
                      )}
                    </div>
                    <div className="past-event-content">
                      {event.category && <span className="event-category">{event.category}</span>}
                      <h3>{event.title}</h3>
                      <div className="event-date">
                        <i className="fas fa-calendar-alt"></i>
                        <span>{event.formattedDate}</span>
                      </div>
                      {event.attendees && (
                        <div className="event-attendees">
                          <i className="fas fa-users"></i>
                          <span>{event.attendees} attendees</span>
                        </div>
                      )}
                      {event.highlights && (
                        <p className="event-highlights">
                          {event.highlights.length > 120 
                            ? `${event.highlights.substring(0, 120)}...` 
                            : event.highlights}
                        </p>
                      )}
                      <motion.button 
                        className="btn btn-outline btn-sm"
                        onClick={() => handleViewDetails(event)}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        View Details <i className="fas fa-arrow-right"></i>
                      </motion.button>
                    </div>
                  </motion.div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Event Detail Modal */}
      <AnimatePresence>
        {showModal && selectedEvent && (
          <motion.div 
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModal}
          >
            <motion.div 
              className="past-event-modal"
              initial={{ scale: 0.9, opacity: 0, y: 30 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 30 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button className="modal-close" onClick={closeModal}>
                <i className="fas fa-times"></i>
              </button>
              
              <div className="modal-image">
                {selectedEvent.image_url && !imageErrors[selectedEvent.id] ? (
                  <img 
                    src={getFullImageUrl(selectedEvent.image_url)} 
                    alt={selectedEvent.title}
                    onError={() => handleImageError(selectedEvent.id)}
                  />
                ) : (
                  <div className="modal-no-image">
                    <i className="fas fa-calendar-alt"></i>
                    <span>No Image Available</span>
                  </div>
                )}
              </div>
              
              <div className="modal-content-inner">
                {selectedEvent.category && (
                  <span className="modal-category">{selectedEvent.category}</span>
                )}
                <h3>{selectedEvent.title}</h3>
                
                <div className="modal-details">
                  <div className="modal-detail">
                    <i className="fas fa-calendar-alt"></i>
                    <span>{selectedEvent.formattedDate}</span>
                  </div>
                  {selectedEvent.event_time && (
                    <div className="modal-detail">
                      <i className="fas fa-clock"></i>
                      <span>{selectedEvent.event_time}</span>
                    </div>
                  )}
                  {selectedEvent.location && (
                    <div className="modal-detail">
                      <i className="fas fa-map-marker-alt"></i>
                      <span>{selectedEvent.location}</span>
                    </div>
                  )}
                  {selectedEvent.attendees && (
                    <div className="modal-detail">
                      <i className="fas fa-users"></i>
                      <span>{selectedEvent.attendees} attendees</span>
                    </div>
                  )}
                  {selectedEvent.max_attendees && (
                    <div className="modal-detail">
                      <i className="fas fa-chair"></i>
                      <span>Capacity: {selectedEvent.max_attendees} people</span>
                    </div>
                  )}
                </div>
                
                {selectedEvent.description && (
                  <div className="modal-description">
                    <h4>About This Event</h4>
                    <p>{selectedEvent.description}</p>
                  </div>
                )}
                
                <div className="modal-actions">
                  <button className="btn-primary" onClick={closeModal}>
                    Close <i className="fas fa-check"></i>
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PastEvents;